import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { PrivacyNote } from "@/components/PrivacyNote";
import { HeartPulse } from "lucide-react";

const METRICS = [
  { key: "mood", labelKey: "checkin.mood", low: "Low", high: "Bright" },
  { key: "energy", labelKey: "checkin.energy", low: "Drained", high: "Energised" },
  { key: "sleep", labelKey: "checkin.sleep", low: "Poor", high: "Restful" },
  { key: "stress", labelKey: "checkin.stress", low: "Calm", high: "Overwhelmed" },
  { key: "anxiety", labelKey: "checkin.anxiety", low: "Settled", high: "Anxious" },
  { key: "confidence", labelKey: "checkin.confidence", low: "Shaky", high: "Steady" },
];

export default function CheckIn() {
  const { t, lang } = useI18n();
  const [q, setQ] = useState("");
  const [vals, setVals] = useState({ mood: 5, energy: 5, sleep: 5, stress: 5, anxiety: 5, confidence: 5 });
  const [answer, setAnswer] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get(`/checkins/prompt?lang=${lang}`).then(({ data }) => setQ(data.question)).catch(() => {}); }, [lang]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/checkins", { ...vals, note, question: q, answer });
      toast.success(t("checkin.doneTitle"));
      setDone(true);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
                  className="mx-auto mt-16 max-w-md text-center">
        <div className="mx-auto flex h-16 w-16 animate-breathe items-center justify-center rounded-full bg-primary">
          <HeartPulse className="h-8 w-8 text-primary-foreground" strokeWidth={1.5} />
        </div>
        <h1 className="mt-6 font-serif text-3xl">{t("checkin.doneTitle")}</h1>
        <p className="mt-2 text-muted-foreground">{t("checkin.doneSub")}</p>
        <Button className="mt-6 rounded-full" onClick={() => { setDone(false); setAnswer(""); setNote(""); }} data-testid="another-checkin-button">{t("checkin.logAnother")}</Button>
      </motion.div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="font-serif text-3xl font-semibold sm:text-4xl">{t("checkin.title")}</h1>
      <p className="mt-2 text-muted-foreground">{t("checkin.subtitle")}</p>
      <PrivacyNote className="mt-3" />

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                  className="ambient-shadow mt-8 rounded-3xl border border-border/60 bg-gradient-to-b from-accent/30 to-card p-6">
        <p className="font-serif text-xl leading-snug">{q || t("checkin.arriving")}</p>
        <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder={t("checkin.answerPh")}
                  data-testid="checkin-answer-input" className="mt-4 min-h-20 resize-none rounded-2xl bg-card" />
      </motion.div>

      <div className="mt-6 space-y-6 rounded-3xl border border-border/60 bg-card p-6 ambient-shadow">
        {METRICS.map((m) => (
          <div key={m.key} data-testid={`metric-${m.key}`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t(m.labelKey)}</span>
              <span className="text-sm font-serif text-primary">{vals[m.key]}/10</span>
            </div>
            <Slider value={[vals[m.key]]} min={0} max={10} step={1}
                    onValueChange={([v]) => setVals((p) => ({ ...p, [m.key]: v }))}
                    className="mt-3" data-testid={`slider-${m.key}`} />
            <div className="mt-1.5 flex justify-between text-xs text-muted-foreground"><span>{m.low}</span><span>{m.high}</span></div>
          </div>
        ))}
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("checkin.notePh")}
                  data-testid="checkin-note-input" className="min-h-20 resize-none rounded-2xl" />
        <Button onClick={save} disabled={saving} className="w-full rounded-full" data-testid="checkin-submit-button">
          {saving ? t("checkin.saving") : t("checkin.submit")}
        </Button>
      </div>
    </div>
  );
}
